import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, OperationType, UpdateStatusType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef } from '../../../../models/grid-context';
import { KeyFile } from '../../../../models/key-file';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { KeyFileModalComponent } from '../../../modals/key-file-modal/key-file-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { Permissions, PermissionSet, ObjectActionListType, ActionType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-container-key-files',
    templateUrl: './container-key-files.component.html',
    styleUrls: ['./container-key-files.component.scss']
})
export class ContainerKeyFileComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    helpIndex: any;

    gridName = 'Container Key Files';
    automationId = 'container-key-file';
    isBusy = false;
    isBusyMessage = '';
    currentHandle = '';
    validChanges = true;
    updateStatusType = UpdateStatusType.KEY_FILE;

    // permissions
    hasViewOnlyPermissions: boolean;
    hasEditPermissions: boolean;
    hasDeletePermissions: boolean;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Type',
            field: 'typeDisplay',
            automationId: this.automationId
        },
        {
            headerName: 'File Name',
            field: 'fileName',
            automationId: this.automationId,
        },
        {
            headerName: 'Modified By',
            field: 'uploadedBy',
            automationId: this.automationId,
            suppressSizeToFit: true
        },
        {
            headerName: 'Modified Date',
            field: 'dateUploaded',
            automationId: this.automationId,
            suppressSizeToFit: true
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.orgManager_listLotusNotesIdFiles,
        responseName: 'idFiles',
        data: {
            requestHandle: this.commonService.currentOrganizationHandle,
            startIndex: 0,
            count: 100
        }
    });

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editContent(item),
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.CONTAINER_KEY_FILES, permissionActions: [ActionType.EDIT] })]
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteContent(item), enabled: (item) => (item.createdBy !== 'System'),
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.CONTAINER_KEY_FILES, permissionActions: [ActionType.DELETE] })]
        })
    ];

    rowClassRules = {
        'disabled-row': (params) => {
            return this.hasViewOnlyPermissions;
        }
    };

    responseArrayCallback = (value: any) => new KeyFile(value);

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof KeyFile) {
            this.editContent(rowNode.data);
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    deleteContent(item: KeyFile): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
    }

    editContent(item: KeyFile): void {
        this.commonService.openModal(KeyFileModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.edit, data: item, currentHandle: this.currentHandle } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    addNewKeyFile(): void {
        this.commonService.openModal(KeyFileModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.new, currentHandle: this.currentHandle } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    selectedTreeItemChanged = (node: GenericTreeNode, projectHandle?: string) => {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = HelpIndex.org_security_container_key_files_dlg;
        this.hasEditPermissions = Permissions.checkUserPermission(ObjectActionListType.CONTAINER_KEY_FILES, ActionType.CREATE);
        this.hasViewOnlyPermissions = Permissions.checkUserPermission(ObjectActionListType.CONTAINER_KEY_FILES) && !this.hasEditPermissions;
        this.hasDeletePermissions = Permissions.checkUserPermission(ObjectActionListType.CONTAINER_KEY_FILES, ActionType.DELETE);

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasViewOnlyPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }

        if (this.currentHandle) {
            this.requestInfo.data = {
                ownerHandle: this.currentHandle
            };
            this.genericGrid.loadData();
        }
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }
}
